import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import ViewModal from '../../components/Vacancies/ViewModal';
import { fetchVacancyById, updateVacancy, fetchVacanciesFunnelStatuses, fetchVacancyHistory, fetchVacancyFunnelStatusesById } from '../../redux/vacanciesSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import { fetchCandidates } from '../../redux/candidatesSlice';
import { fetchCandidatesByVacancy } from '../../redux/vacancyCandidatesSlice';
import { canCommentOnVacancy } from '../../utils/vacancyPermissions';

const VacancyDetailsPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const vacanciesState = useSelector((state) => state.vacancies);
    const { current: vacancy, funnelStatuses, isLoading: vacancyLoading } = vacanciesState;
    const { list: users, loading: usersLoading } = useSelector((state) => state.userOrganisations);
    const { organisations, isLoading: organisationsLoading } = useSelector((state) => state.organisationDepartment);
    const candidatesState = useSelector((state) => state.candidates);
    const allCandidates = candidatesState?.list || [];
    const candidatesLoading = !!(candidatesState?.loading || candidatesState?.isLoading);
    const { candidatesByVacancy } = useSelector((state) => state.vacancyCandidates);
    const allVacancies = vacanciesState?.list || [];
    const history = vacanciesState?.history || [];
    const historyLoading = !!vacanciesState?.isLoading;
    const funnelConfig = vacanciesState?.vacancyFunnelStatuses || [];
    const funnelConfigLoading = !!vacanciesState?.isLoading;
    const funnelConfigError = vacanciesState?.error || null;
    const { orgId, role } = useSelector((state) => state.auth);

    const loading = vacancyLoading || usersLoading || organisationsLoading;

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                const vacancyId = parseInt(id);
                await Promise.all([
                    dispatch(fetchVacancyById({ id: vacancyId, orgId: orgId })).unwrap(),
                    users?.length ? Promise.resolve() : dispatch(fetchUsers()).unwrap(),
                    dispatch(fetchVacanciesFunnelStatuses()).unwrap(),
                    dispatch(fetchVacancyHistory({ id: vacancyId, organisationId: orgId })).unwrap(),
                    dispatch(fetchVacancyFunnelStatusesById(vacancyId)).unwrap(),
                    allCandidates?.length ? Promise.resolve() : dispatch(fetchCandidates({ skip: 0, limit: 1000, org_id: orgId })).unwrap(),
                    dispatch(fetchCandidatesByVacancy(vacancyId)).unwrap(),
                    // organisations уже загружены в Layout из auth.userOrganisations
                ]);
            } catch (e) {
                message.error('Не удалось загрузить данные по вакансии');
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, orgId]);

    const refreshVacancyHistory = useCallback(async () => {
        if (!id) return;
        try {
            await dispatch(fetchVacancyHistory({ id: parseInt(id), organisationId: orgId })).unwrap();
        } catch (_) { }
    }, [dispatch, id, orgId]);

    const refreshVacancyCandidates = useCallback(async () => {
        if (!id) return;
        try {
            await dispatch(fetchCandidatesByVacancy(parseInt(id))).unwrap();
        } catch (_) { }
    }, [dispatch, id]);

    const refreshFunnelConfig = useCallback(async () => {
        if (!id) return;
        try {
            await dispatch(fetchVacancyFunnelStatusesById(parseInt(id))).unwrap();
        } catch (_) { }
    }, [dispatch, id]);

    const handleClose = useCallback(() => {
        navigate('/vacancies');
    }, [navigate]);

    const handleCommentSubmit = useCallback(async (vacancyId, comment) => {
        if (!canCommentOnVacancy(role)) {
            message.warning('У вас нет прав для добавления комментариев к вакансии');
            return;
        }
        try {
            await dispatch(updateVacancy({ id: vacancyId, organisationId: orgId, vacancy: { comment } })).unwrap();
            await dispatch(fetchVacancyHistory({ id: vacancyId, organisationId: orgId })).unwrap();
        } catch (e) {
            message.error('Ошибка при сохранении комментария');
        }
    }, [dispatch, orgId, role]);

    const handleReminderSubmit = useCallback((id, datetime) => {
        // Reminder is handled inside modal UI; keep stub here if needed
        console.log('Reminder set for vacancy', id, datetime?.toISOString?.());
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!vacancy) return null;

    return (
        <div style={{ padding: 24 }}>
            <ViewModal
                isOpen={true}
                onClose={handleClose}
                onCommentSubmit={handleCommentSubmit}
                onReminderSubmit={handleReminderSubmit}
                vacancy={vacancy}
                organisations={organisations}
                users={users}
                funnelStatuses={funnelStatuses}
                // moved data fetching to parent; provide data and refreshers
                history={history}
                historyLoading={historyLoading}
                allVacancies={allVacancies}
                candidatesByVacancy={candidatesByVacancy}
                allCandidates={allCandidates}
                candidatesLoading={candidatesLoading}
                funnelConfig={funnelConfig}
                funnelConfigLoading={funnelConfigLoading}
                funnelConfigError={funnelConfigError}
                orgId={orgId}
                refreshVacancyHistory={refreshVacancyHistory}
                refreshVacancyCandidates={refreshVacancyCandidates}
                refreshFunnelConfig={refreshFunnelConfig}
            />
        </div>
    );
};

export default VacancyDetailsPage;


